// UPPDRAG-62 PR-D: "What is the current user's team in this league?" hook.
//
// Replaces the inline pattern previously used in LeagueDetail.tsx,
// LeagueDetailPanel.tsx (and similar):
//   useQuery(["my-team-in-league", leagueId, user.id], async () =>
//     supabase.from("teams").select("*")
//       .eq("league_id", leagueId)
//       .or(`player1_id.eq.${user.id},player2_id.eq.${user.id}`)
//       .maybeSingle()
//   )
//
// The legacy pattern silently dropped registered roster members joined
// via UPPDRAG-60's customer_id flow (those user_ids live in team_members,
// not teams.player1/2).
//
// TASK-66 PR-D: dropped the `role` field + `MyTeamRole` type. Captain
// concept is gone (Q4 — captain only; player1/2 stays). Zero downstream
// consumers of `role` per the investigation audit. Returns null when
// the user has no team in the league.
//
// TASK-67 PR-D: identity resolution collapses to team_members ONLY.
// Previously a deduped union of (a) teams.player1/2 + (b) team_members.user_id
// — PR-A's backfill ensured every player1/2 user_id has a team_members
// row, so branch (a) is redundant. fetchTeamsForUser already returns the
// team_members-only set (see src/lib/teamMembershipQueries.ts).

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { fetchTeamsForUser } from "@/lib/teamMembershipQueries";
import type { Tables } from "@/integrations/supabase/types";

type TeamRow = Tables<"teams">;

export interface UseMyTeamForLeagueResult {
  team: TeamRow | null;
  isLoading: boolean;
}

/**
 * Returns the current user's team in `leagueId` (or null). See file
 * header for resolution semantics.
 */
export function useMyTeamForLeague(
  leagueId: string | null | undefined,
): UseMyTeamForLeagueResult {
  const { user } = useAuth();

  const { data, isLoading } = useQuery<TeamRow | null>({
    queryKey: ["my-team-for-league", leagueId, user?.id],
    queryFn: async () => {
      if (!leagueId || !user) return null;
      const teams = await fetchTeamsForUser<TeamRow>(supabase, user.id, {
        leagueId,
        // limit=1 — a user can only be on one team per league (CHECK
        // constraint on team_members + manual invariant on teams).
        // We over-fetch by 1 to detect violations defensively, but the
        // hook returns the first match.
        limit: 1,
      });
      return teams[0] ?? null;
    },
    enabled: !!leagueId && !!user,
  });

  const team = data ?? null;

  return { team, isLoading };
}
